import React, { useMemo, useState } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor, useSensor, useSensors, useDroppable, closestCorners,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import clsx from 'clsx';
import OtTarjeta from './OtTarjeta';
import { agruparPorFase, equipoDe } from '../../utils/ordenesTrabajo';

function Columna({ fase, equipo, rows, miembros, onOpen, arrastrable }) {
  const { setNodeRef, isOver } = useDroppable({ id: `fase:${fase.id}` });
  return (
    <div
      className={clsx(
        'flex flex-col w-72 min-w-[288px] bg-zinc-50 rounded-xl border transition-all',
        isOver ? 'border-azul-surelia ring-2 ring-azul-surelia/20' : 'border-zinc-200',
      )}
    >
      <div className="flex items-center gap-2 p-3 rounded-t-xl bg-azul-surelia/5">
        <div className="flex-1 min-w-0">
          <span className="font-medium text-sm text-azul-surelia">{fase.nombre}</span>
          {equipo && (
            <span className="block text-xs text-zinc-500 truncate">{equipo.nombre}</span>
          )}
          {fase.diasObjetivo && (
            <span className="block text-[11px] text-zinc-400 tabular-nums">objetivo {fase.diasObjetivo} d</span>
          )}
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-white font-medium text-azul-surelia tabular-nums">
          {rows.length}
        </span>
      </div>
      <div ref={setNodeRef} className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-330px)] min-h-[150px]">
        <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          {rows.map((ot) => (
            <OtTarjeta key={ot.id} ot={ot} miembros={miembros} onClick={onOpen} arrastrable={arrastrable} />
          ))}
        </SortableContext>
        {rows.length === 0 && (
          <div className="h-full min-h-[120px] flex items-center justify-center text-xs text-zinc-400">
            Sin órdenes
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The kanban: one column per configured fase, OTs dragged between them.
 *
 * @param {Object} props
 * @param {Array<Object>} props.rows  - open, filtered OTs of ONE tipo
 * @param {Array<Object>} props.fases  - that tipo's flow
 * @param {{equipos:Array}} props.config
 * @param {Array<Object>} props.miembros
 * @param {Function} props.onMove  - (ot, faseId); the coordinador's relevo
 * @param {Function} props.onOpen  - (ot)
 * @param {boolean} [props.puedeMover]  - false renders the cards without drag
 */
export function OtTablero({ rows, fases, config, miembros, onMove, onOpen, puedeMover = true }) {
  const [activa, setActiva] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const { columnas, sinFase } = useMemo(() => agruparPorFase(rows, fases), [rows, fases]);
  const porId = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.id] = r; });
    return m;
  }, [rows]);

  const faseDeItem = (id) => porId[id]?.faseId || null;

  const handleDragEnd = async ({ active, over }) => {
    setActiva(null);
    if (!over) return;
    const ot = porId[active.id];
    if (!ot) return;
    let target = null;
    if (String(over.id).startsWith('fase:')) target = String(over.id).slice(5);
    else target = faseDeItem(over.id);
    if (!target || target === ot.faseId) return;
    try {
      await onMove(ot, target);
    } catch (e) {
      console.error('mover OT failed', e);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={({ active }) => setActiva(porId[active.id] || null)}
      onDragCancel={() => setActiva(null)}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-3 overflow-x-auto pb-3">
        {columnas.map(({ fase, rows: filas }) => (
          <Columna
            key={fase.id}
            fase={fase}
            equipo={fase.equipoId ? equipoDe(config.equipos, fase.equipoId) : null}
            rows={filas}
            miembros={miembros}
            onOpen={onOpen}
            arrastrable={puedeMover}
          />
        ))}
        {sinFase.length > 0 && (
          <Columna
            fase={{ id: '__sin_fase', nombre: 'Sin fase' }}
            equipo={null}
            rows={sinFase}
            miembros={miembros}
            onOpen={onOpen}
            arrastrable={puedeMover}
          />
        )}
      </div>
      <DragOverlay dropAnimation={null}>
        {activa ? <div className="w-72"><OtTarjeta ot={activa} miembros={miembros} overlay /></div> : null}
      </DragOverlay>
    </DndContext>
  );
}

export default OtTablero;
